// @givmo/fundraisers-module — fundraiser share-QR download helpers (CPB-WEB1).
//
// The fundraiser share QR (`…/fundraisers/{id}/share/qr?format=svg|png|pdf`) returns the rendered code as a
// BINARY body (`Blob | File`), so — exactly like the §321 CSV exports (`features/grants/csv-download.ts`)
// and the super-admin ZIP export — it needs the response-mode opt-out: `parseAs:'blob'` +
// `responseStyle:'fields'` (get the raw `Response` so the Content-Disposition filename can be read) via
// the package's `readBinaryMeta`. Authenticated: it rides the configured api-client (Bearer + App Check),
// never a hand-rolled fetch. Kept local to the fundraisers feature (the grants CSV helper is grants-shaped).

import { readBinaryMeta, type Options } from '@givmo/api-client';

/** The share-QR download formats (the backend `QrRenderFormatParam`, not re-exported from the barrel). */
export type ShareQrFormat = 'svg' | 'png' | 'pdf';

/**
 * Per-request fragment opting a single QR render OUT of JSON parsing and into a BINARY (blob) response
 * WITH the raw `Response` exposed (`responseStyle:'fields'`), so the caller can read the
 * Content-Disposition filename via {@link readBinaryMeta}. Same shape the grants CSV export uses.
 */
export const qrDownloadOptions = {
  parseAs: 'blob',
  responseStyle: 'fields',
} as const satisfies Pick<Options, 'parseAs' | 'responseStyle'>;

/** The result shape of a configured (non-throwing) SDK call with `responseStyle:'fields'`. */
interface BinaryResult {
  data?: unknown;
  error?: unknown;
  response?: Response;
}

/** Any function that, given the blob response-mode options, runs one QR render SDK call. */
export type QrRenderCall = (options: typeof qrDownloadOptions) => Promise<BinaryResult>;

/** Trigger an in-browser download of `blob` as `filename` via a transient object-URL anchor. */
export function blobSave(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Run one fundraiser share-QR render and save it. `call` receives {@link qrDownloadOptions} and returns the
 * configured SDK result (`{ data, error, response }`). Reads the server's Content-Disposition filename
 * when present, else uses `fallbackFilename`. Throws on an errored or missing response so the caller
 * surfaces a toast and never triggers a phantom download.
 */
export async function runQrDownload(call: QrRenderCall, fallbackFilename: string): Promise<void> {
  const result = await call(qrDownloadOptions);
  if (result.error) throw result.error;
  if (!result.response) throw new Error('QR download failed: no response.');

  const meta = readBinaryMeta(result.response);
  const filename = meta.filename ?? fallbackFilename;
  // `data` is the Blob (parseAs:'blob'); guard defensively for a non-blob body.
  const blob =
    result.data instanceof Blob
      ? result.data
      : new Blob([(result.data ?? '') as BlobPart], { type: 'application/octet-stream' });
  blobSave(blob, filename);
}

/** Synthesize a fallback QR filename (stamped) when the response carries no Content-Disposition. */
export function fallbackQrName(format: ShareQrFormat): string {
  const stamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `givmo_fundraiser_qr_${stamp}.${format}`;
}
